import React, { useCallback, useState } from "react";
import { inject, observer } from "mobx-react";
import ProfileStore from "common/my-page/stores/profile/ProfileStore";
import ValidationStore from "library/core/stores/validation/ValidationStore";
import LanguageStore from "library/core/stores/language/LanguageStore";
import TailwindFlex from "library/components/_tailwind/flex";
import TailwindButton from "library/components/_tailwind/button";
import TailwindInput from "library/components/_tailwind/input";
import TailwindText from "library/components/_tailwind/text";
import TailwindTranslatedText from "library/components/_tailwind/translated-text";
import { TailwindInputType } from "library/components/_tailwind/input-base";
import TailwindIcon from "library/components/_tailwind/icon";

type Props = {
  profileStore?: ProfileStore;
  validationStore?: ValidationStore;
  languageStore?: LanguageStore;
};

const MobileBroadcastSupportForm: React.ComponentType<Props> = ({
  profileStore,
  validationStore,
  languageStore,
}) => {
  const { intl } = languageStore!;
  const { profile } = profileStore!;
  const { clearError, validate, errors, validateMultipleObj } =
    validationStore!;

  const [username, setUsername] = useState<string>(profile?.username || "");
  const [email, setEmail] = useState<string>("");
  const [message, setMessage] = useState<string>("");
  const [submitted, setSubmitted] = useState<boolean>(false);

  const handleSubmit = useCallback(
    (event: React.FormEvent) => {
      event.preventDefault();
      const isValid = validateMultipleObj({ username, email, message });
      if (!isValid) return;

      const chatContainer = document.querySelector(
        ".__8x8Chat-Container"
      ) as HTMLElement | null;
      const chatHeight = chatContainer?.style.height;
      const chatIsClosed =
        !chatContainer ||
        !chatHeight ||
        chatHeight === "0" ||
        chatHeight === "0px";
      const btn = document.querySelector(
        ".live_chat_button_8x8 img"
      ) as HTMLImageElement;
      if (btn && chatIsClosed) {
        btn.click();
      }
      setSubmitted(true);
    },
    [username, email, message, validateMultipleObj]
  );

  if (submitted) {
    return (
      <TailwindFlex
        flexDirection='flex-col'
        alignItems='items-center'
        justifyContent='justify-center'
        height='h-full'
        gap='gap-3'
        padding={["px-6"]}>
        <TailwindIcon
          name='check_circle'
          fontSize='text-4xl'
          style={{ color: "#22c55e" }}
        />
        <TailwindText
          textAlign='text-center'
          fontSize='text-sm'
          textColor='text-main-color'>
          {intl.formatMessage({
            id: "broadcast.support.submitted",
            defaultMessage:
              "Your support request has been sent. A live agent will connect with you shortly.",
          })}
        </TailwindText>
        <TailwindButton
          fullWidth={false}
          rounded={false}
          padding={["px-6"]}
          margin={["mt-2"]}
          backgroundColor='primary'
          justifyContent='justify-center'
          textColor='text-white'
          onClick={() => {
            setSubmitted(false);
            setMessage("");
          }}>
          {intl.formatMessage({
            id: "broadcast.support.sendAnother",
            defaultMessage: "Send Another",
          })}
        </TailwindButton>
      </TailwindFlex>
    );
  }

  return (
    <TailwindFlex
      flexDirection='flex-col'
      padding={["px-4", "py-3"]}
      height='h-full'
      overflow={["overflow-y-auto"]}>
      <TailwindText
        fontSize='text-base'
        fontWeight='font-semibold'
        textColor='text-main-color'
        margin={["mb-1"]}>
        {intl.formatMessage({
          id: "preLiveChatForm.heading",
          defaultMessage: "Live Support",
        })}
      </TailwindText>
      <TailwindText
        fontSize='text-sm'
        textColor='text-main-color'
        margin={["mb-4"]}
        style={{ opacity: 0.7 }}>
        {intl.formatMessage({
          id: "preLiveChatForm.contact-us-below",
          defaultMessage: "For live support, please contact us below:",
        })}
      </TailwindText>

      <form onSubmit={handleSubmit} className='flex flex-col gap-3 flex-grow'>
        <TailwindInput
          required
          rounded={false}
          backgroundColor='transparent'
          borderColor='border-gray-300'
          borderWidth={["border"]}
          label={intl.formatMessage({
            id: "common.username",
            defaultMessage: "Username",
          })}
          labelProps={{ margin: [] }}
          errorProps={{ textAlign: "text-right" }}
          value={username}
          type={TailwindInputType.text}
          name='username'
          onChange={e => {
            clearError("username");
            setUsername(e.target.value);
          }}
          error={errors?.username || undefined}
          onBlur={() => validate("username", username)}
        />
        <TailwindInput
          required
          rounded={false}
          backgroundColor='transparent'
          borderColor='border-gray-300'
          borderWidth={["border"]}
          label={intl.formatMessage({
            id: "common.email",
            defaultMessage: "Email",
          })}
          labelProps={{ margin: [] }}
          errorProps={{ textAlign: "text-right" }}
          value={email}
          type={TailwindInputType.email}
          name='email'
          onChange={e => {
            clearError("email");
            setEmail(e.target.value);
          }}
          error={errors?.email || undefined}
          onBlur={() => validate("email", email)}
        />
        <TailwindInput
          required
          rounded={false}
          backgroundColor='transparent'
          borderColor='border-gray-300'
          borderWidth={["border"]}
          label={intl.formatMessage({
            id: "preLiveChatForm.your-message",
            defaultMessage: "Your Message",
          })}
          labelProps={{ margin: [] }}
          boxProps={{ height: "h-auto" }}
          errorProps={{ textAlign: "text-right" }}
          value={message}
          type={TailwindInputType.textarea}
          rows={4}
          minRows={4}
          maxRows={4}
          maxLength={2000}
          name='reason_for_contact'
          onChange={e => {
            clearError("message");
            setMessage(e.target.value);
          }}
          error={errors?.message || undefined}
          onBlur={() => validate("message", message)}
        />
        <TailwindButton
          type='submit'
          margin={["mt-2"]}
          fullWidth
          size='lg'
          backgroundColor='primary'
          justifyContent='justify-center'
          textColor='text-white'
          rounded={false}>
          <TailwindTranslatedText
            descriptor={{
              id: "common.submit",
              defaultMessage: "Submit",
            }}
            textTransform='uppercase'
            fontWeight='font-bold'
          />
        </TailwindButton>
      </form>
    </TailwindFlex>
  );
};

export default inject(
  "profileStore",
  "validationStore",
  "languageStore"
)(observer(MobileBroadcastSupportForm));
